import { useEffect, useId, useRef } from 'react';

import type { KeyboardEvent } from 'react';

import styles from './AddCatalogueLinkForm.module.css';

import { Button } from 'components/Buttons';
import { FormField } from 'components/FormField';
import { Label } from 'components/Label';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';
import { InputField } from 'components/InputField';
import { useLanguage } from 'components/RteEditor/Providers/LanguageProvider';
import { Repeater } from 'components/Repeater';

type SearchResult = {
  code: string;
  id: string;
  name: {
    EN: string;
    NL: string;
  };
  programmeType: string;
};

export type SearchResponse = {
  state: 'idle' | 'pending' | 'ready';
  results: SearchResult[];
};

interface AddCatalogueLinkFormProps {
  linkType: 'course' | 'programme';
  onCloseHandler: () => void;
  onSearchHandler: (query: string, type: 'course' | 'programme') => void;
  searchResponse?: SearchResponse;
  onSelect?: (code: string, label: string, type: 'course' | 'programme') => void;
}

export const AddCatalogueLinkForm = ({
  linkType,
  onCloseHandler,
  onSearchHandler,
  searchResponse,
  onSelect,
}: AddCatalogueLinkFormProps) => {
  const t = useTranslation();
  const searchId = `search_${useId()}`;
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const resultListRef = useRef<HTMLDivElement>(null);
  const { currentLanguage } = useLanguage();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    if (onSearchHandler) {
      onSearchHandler(query, linkType);
    }
  };

  const handleClose = () => {
    onCloseHandler();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const container = resultListRef.current;
      const firstFocusable = container?.querySelector<HTMLElement>('button');
      firstFocusable?.focus();
    }
  };

  const onResultKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    code: string,
    name: string,
    linkType: 'course' | 'programme'
  ) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        handleClose();
        break;
      case 'Enter':
        event.preventDefault();
        onSelect && onSelect(code, name, linkType);
        break;
      case 'ArrowDown': {
        event.preventDefault();
        event.stopPropagation();
        const nextElement = event.currentTarget.nextElementSibling;
        (nextElement as HTMLButtonElement)?.focus();
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        event.stopPropagation();
        const previousElement = event.currentTarget.previousElementSibling;
        (previousElement as HTMLButtonElement)?.focus();
        break;
      }
      case 'Tab':
        event.preventDefault();
        handleClose();
        break;
      default:
        event.preventDefault();
        break;
    }
  };

  useEffect(() => {
    if (searchFieldRef.current) {
      onSearchHandler('', linkType);
      searchFieldRef.current.value = '';
      searchFieldRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles['courselinkform']}>
      <Repeater gap="050">
        <FormField className={styles['courselinkform__input-field']}>
          <Label htmlFor={searchId}>
            {t.cataloguelinks[linkType === 'course' ? 'searchCourseFieldLabel' : 'searchProgrammeFieldLabel']}
          </Label>
          <div className={styles['linkform__input-url-wrapper']}>
            <InputField
              id={searchId}
              placeholder={t.cataloguelinks.searchFieldPlaceholder}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              ref={searchFieldRef}
              aria-label={t.aria.labels.searchField}
            />
          </div>
        </FormField>
        {searchResponse && searchResponse.state === 'pending' && (
          <div className={styles['courselinkform__result-message']}>{t.cataloguelinks.loading}</div>
        )}
        {searchResponse && searchResponse.results.length === 0 && searchResponse.state === 'ready' && (
          <div className={styles['courselinkform__result-message']}>{t.cataloguelinks.noResults}</div>
        )}
        {searchResponse && searchResponse.results.length > 0 && (
          <div className={styles['courselinkform__results-wrapper']}>
            <div className={styles['courselinkform__results-header']}>
              <span>
                {
                  t.cataloguelinks[
                    linkType === 'course' ? 'resultCourseListCatalogNumber' : 'resultProgrammeListCatalogNumber'
                  ]
                }
              </span>
              <span>{t.cataloguelinks.resultListName}</span>
            </div>
            <div className={styles['courselinkform__results']} ref={resultListRef}>
              {searchResponse.results.map(({ id, code, name }) => (
                <button
                  key={id}
                  type="button"
                  className={styles['courselinkform__result-item']}
                  onClick={() => onSelect && onSelect(code, name[currentLanguage], linkType)}
                  onKeyDown={(event) => onResultKeyDown(event, code, name[currentLanguage], linkType)}
                  tabIndex={0}
                  aria-label={`${code} ${name[currentLanguage]}`}
                >
                  <span>{code}</span>
                  <span>{name[currentLanguage]}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Repeater>
      <div className={styles['courselinkform__footer']}>
        <Button variant={'secondary'} onClick={handleClose} aria-label={t.common.cancel} type="button">
          {t.common.cancel}
        </Button>
      </div>
    </div>
  );
};
